import { Box, useColorModeValue } from '@chakra-ui/react';
import { ReactElement } from 'react';

interface IllustrationProps {
	size?: number;
}

/** Wrapper that applies brand color + subtle bg circle */
function IllustrationWrapper({ size = 140, children }: IllustrationProps & { children: ReactElement }) {
	const strokeColor = useColorModeValue('#023F7F', '#6D9FD3');
	const fillColor = useColorModeValue('rgba(2, 63, 127, 0.06)', 'rgba(109, 159, 211, 0.08)');
	const circleBg = useColorModeValue('rgba(2, 63, 127, 0.04)', 'rgba(109, 159, 211, 0.04)');

	return (
		<Box
			w={`${size + 20}px`}
			h={`${size + 20}px`}
			borderRadius="50%"
			bg={circleBg}
			display="flex"
			alignItems="center"
			justifyContent="center"
		>
			<svg
				width={size}
				height={size}
				viewBox="0 0 120 120"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				style={{ color: strokeColor, '--fill-accent': fillColor } as React.CSSProperties}
			>
				{children}
			</svg>
		</Box>
	);
}

/** Calendar — for games and events */
export function CalendarIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Calendar body */}
				<rect x="22" y="32" width="76" height="66" rx="8" stroke="currentColor" strokeWidth="2" />
				{/* Top bar */}
				<path d="M22 50L98 50" stroke="currentColor" strokeWidth="1.5" />
				{/* Pegs */}
				<path d="M42 24L42 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
				<path d="M78 24L78 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
				{/* Date dots */}
				<circle cx="40" cy="65" r="3" fill="currentColor" opacity="0.15" />
				<circle cx="60" cy="65" r="3" fill="currentColor" opacity="0.15" />
				<circle cx="80" cy="65" r="3" fill="currentColor" opacity="0.15" />
				<circle cx="40" cy="83" r="3" fill="currentColor" opacity="0.15" />
				{/* Active date highlight */}
				<circle cx="60" cy="83" r="8" fill="var(--fill-accent)" />
				{/* Checkmark on active date */}
				<path d="M56 83L59 86L65 79" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				<circle cx="80" cy="83" r="3" fill="currentColor" opacity="0.15" />
			</g>
		</IllustrationWrapper>
	);
}

/** People — for users, members, cohorts */
export function PeopleIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Center person */}
				<circle cx="60" cy="38" r="13" stroke="currentColor" strokeWidth="2" />
				<path d="M36 92C36 74 48 62 60 62C72 62 84 74 84 92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				{/* Left person (receded) */}
				<circle cx="28" cy="46" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
				<path d="M12 86C12 72 20 64 28 64C36 64 44 72 44 86" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
				{/* Right person (receded) */}
				<circle cx="92" cy="46" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
				<path d="M76 86C76 72 84 64 92 64C100 64 108 72 108 86" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
			</g>
		</IllustrationWrapper>
	);
}

/** Court — for courts and venues */
export function CourtIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Outer court */}
				<rect x="16" y="22" width="88" height="76" rx="4" stroke="currentColor" strokeWidth="2" />
				{/* Center line */}
				<line x1="60" y1="22" x2="60" y2="98" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
				{/* Service boxes */}
				<rect x="16" y="36" width="44" height="48" stroke="currentColor" strokeWidth="1" opacity="0.25" />
				<rect x="60" y="36" width="44" height="48" stroke="currentColor" strokeWidth="1" opacity="0.25" />
				{/* Center circles */}
				<circle cx="60" cy="60" r="12" stroke="currentColor" strokeWidth="1" opacity="0.2" />
				<circle cx="60" cy="60" r="3" fill="currentColor" opacity="0.15" />
			</g>
		</IllustrationWrapper>
	);
}

/** Chart — for analytics, pricing insights, churn prediction */
export function ChartIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Axes */}
				<path d="M26 92L26 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				<path d="M26 92L98 92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				{/* Bars */}
				<rect x="34" y="70" width="12" height="22" rx="3" fill="var(--fill-accent)" />
				<rect x="52" y="52" width="12" height="40" rx="3" fill="var(--fill-accent)" />
				<rect x="70" y="38" width="12" height="54" rx="3" fill="var(--fill-accent)" />
				<rect x="88" y="58" width="12" height="34" rx="3" fill="var(--fill-accent)" />
				{/* Trend line */}
				<path d="M40 64L58 46L76 32L94 50" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
				{/* Dot at peak */}
				<circle cx="76" cy="32" r="3.5" fill="currentColor" opacity="0.3" />
				<circle cx="76" cy="32" r="1.5" fill="currentColor" />
			</g>
		</IllustrationWrapper>
	);
}

/** Video — for court recordings and media */
export function VideoIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Screen/frame */}
				<rect x="18" y="28" width="84" height="56" rx="8" stroke="currentColor" strokeWidth="2" />
				{/* Play triangle */}
				<path d="M52 48L52 72L72 60Z" fill="var(--fill-accent)" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
				{/* Stand */}
				<path d="M48 84L48 92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				<path d="M72 84L72 92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				<path d="M40 92L80 92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				{/* Recording dot */}
				<circle cx="90" cy="38" r="3" fill="currentColor" opacity="0.3" />
			</g>
		</IllustrationWrapper>
	);
}

/** Inbox — default empty state */
export function InboxIllustration(props: IllustrationProps) {
	return (
		<IllustrationWrapper {...props}>
			<g>
				{/* Box sides */}
				<path d="M26 54L26 88C26 92 29 95 33 95L87 95C91 95 94 92 94 88L94 54" stroke="currentColor" strokeWidth="2" fill="none" />
				{/* Box opening */}
				<path d="M16 54L36 30L84 30L104 54" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round" />
				{/* Inner shelf */}
				<path d="M26 54L42 54C45 54 47 57 47 60L47 63C47 67 50 70 54 70L66 70C70 70 73 67 73 63L73 60C73 57 75 54 78 54L94 54" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.4" />
				{/* Floating elements */}
				<circle cx="52" cy="42" r="2" fill="currentColor" opacity="0.15" />
				<circle cx="68" cy="38" r="2" fill="currentColor" opacity="0.15" />
				<path d="M57 44L63 44" stroke="currentColor" strokeWidth="1.5" opacity="0.15" strokeLinecap="round" />
			</g>
		</IllustrationWrapper>
	);
}
